import React from "react";
import {
  Document,
  Font,
  Image,
  Page,
  Polygon,
  StyleSheet,
  Svg,
  Text,
  View,
} from "@react-pdf/renderer";
import HNLNavLogo from "@/assets/certificates/hnl-logo.png";
import { CLIMB_PLAN_PDF_ICONS as icons } from "./climbPlanPDFIcons";
import type { ClimbPlanPDFData } from "@/pages/planner/climb-plan/review/pdf/climbPlanPDFData";
import { reviewPrompt } from "@/pages/planner/climb-plan/review/reviewPrompt";
import { tidyReflection } from "@/pages/planner/climb-plan/review/tidyReflection";
import { climbPlanPDFTexts as texts } from "./climbPlanPDFTexts";
import {
  PDF_ACCENT_BAR_HEIGHT,
  PDF_COLOR,
  PDF_FONT,
  PDF_FOOTER_HEIGHT,
  PDF_HEADER_HEIGHT,
  PDF_TYPE,
} from "./pdfTheme";

// @ts-ignore — .ttf
import ProximaNovaReg from "@/assets/fonts/proximanova_regular.ttf";
// @ts-ignore — .otf
import ProximaNovaBold from "@/assets/fonts/proximanova_bold.otf";
// @ts-ignore — .ttf
import MrDafoe from "@/assets/fonts/MrDafoe-Regular.ttf";

Font.register({ family: PDF_FONT.regular, src: ProximaNovaReg });
Font.register({
  family: PDF_FONT.bold,
  src: ProximaNovaBold,
  fontWeight: "bold",
});
Font.register({ family: PDF_FONT.signature, src: MrDafoe });

/** The sample course sequence's row height, shared by the slab and its point. */
const COURSE_HEIGHT = 20;

const styles = StyleSheet.create({
  page: {
    backgroundColor: PDF_COLOR.white,
    fontFamily: PDF_FONT.regular,
    fontSize: PDF_TYPE.body,
    color: PDF_COLOR.text,
    paddingTop: PDF_ACCENT_BAR_HEIGHT + PDF_HEADER_HEIGHT,
    paddingBottom: PDF_FOOTER_HEIGHT + 12,
    paddingHorizontal: 32,
  },

  accentBar: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: PDF_ACCENT_BAR_HEIGHT,
    backgroundColor: PDF_COLOR.accent,
  },
  header: {
    position: "absolute",
    top: PDF_ACCENT_BAR_HEIGHT + 12,
    left: 32,
    right: 32,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  logo: { width: 90, height: 18.816 },
  headerRight: { fontSize: PDF_TYPE.small, color: PDF_COLOR.muted },

  footer: {
    position: "absolute",
    bottom: 14,
    left: 32,
    right: 32,
    flexDirection: "row",
    justifyContent: "space-between",
    fontSize: PDF_TYPE.small,
    color: PDF_COLOR.muted,
  },

  // --- Sections ---
  section: { marginTop: 16 },
  sectionTitle: {
    fontFamily: PDF_FONT.bold,
    fontSize: PDF_TYPE.sectionTitle,
    color: PDF_COLOR.muted,
    letterSpacing: 0.6,
  },
  sectionSubtitle: {
    fontSize: PDF_TYPE.small,
    color: PDF_COLOR.muted,
    marginTop: 4,
  },

  heading: {
    fontFamily: PDF_FONT.bold,
    fontSize: PDF_TYPE.heading,
    marginBottom: 2,
  },
  subheading: {
    fontFamily: PDF_FONT.bold,
    fontSize: PDF_TYPE.subheading,
    marginTop: 10,
    marginBottom: 4,
  },
  // No italic: the only Proxima Nova faces this repo ships in a format
  // @react-pdf can read are regular and bold, and asking for a style it cannot
  // resolve is a hard throw, not a fallback. Colour carries the distinction.
  caption: { fontSize: PDF_TYPE.small, color: PDF_COLOR.muted },
  body: { fontSize: PDF_TYPE.body, lineHeight: 1.45 },
  paragraph: { fontSize: PDF_TYPE.body, lineHeight: 1.45, marginTop: 4 },

  row: { flexDirection: "row", alignItems: "center" },
  /**
   * The text beside an image in a row.
   *
   * `flex: 1` and NOT `flexGrow: 1`. Grow alone leaves the basis at `auto`, so
   * the box measures itself against its own content rather than the space
   * left over — and a long line then runs off the right edge of the page
   * instead of wrapping. `flex` sets grow, shrink and a zero basis together,
   * which is what bounds it to the row.
   */
  rowBody: { flex: 1, minWidth: 0 },
  twoColumn: { flexDirection: "row", flexWrap: "wrap" },
  half: { width: "50%", paddingRight: 10 },
  /** The same column with nothing to sit beside it. */
  full: { width: "100%" },

  facetRow: { flexDirection: "row", alignItems: "center", marginTop: 5 },
  facetIcon: { width: 14, height: 14, marginRight: 5 },
  facetName: { fontFamily: PDF_FONT.bold, fontSize: PDF_TYPE.body },
  facetTier: {
    fontSize: PDF_TYPE.small,
    color: PDF_COLOR.muted,
    marginLeft: 4,
  },

  // The career plan's own scored-result row: a round icon, the name with its
  // level ranged right, and a filled bar in the facet's CMS colour.
  scoredRow: { flexDirection: "row", alignItems: "flex-start", marginTop: 16 },
  scoredIcon: {
    width: 22,
    height: 22,
    borderRadius: 11,
    marginRight: 8,
    backgroundColor: PDF_COLOR.white,
  },
  scoredHead: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  scoredName: { fontSize: PDF_TYPE.body, flex: 1, minWidth: 0 },
  scoredLevel: {
    fontFamily: PDF_FONT.bold,
    fontSize: PDF_TYPE.small,
    color: PDF_COLOR.muted,
  },
  /** The bar and the level that reads it, on one line. */
  scoredBarRow: { flexDirection: "row", alignItems: "center", marginTop: 5 },
  scoredTrack: {
    flex: 1,
    height: 6,
    backgroundColor: PDF_COLOR.surface,
    borderRadius: 3,
  },
  scoredFill: { height: 6, borderRadius: 3 },
  scoredBarLevel: { marginLeft: 8 },
  /** The Top Hat's fit message, called out so it is not lost under the list. */
  fitBox: {
    marginTop: 8,
    padding: 8,
    backgroundColor: PDF_COLOR.purpleSurface,
    borderRadius: 6,
  },
  fitBoxLabel: {
    fontFamily: PDF_FONT.bold,
    fontSize: 7,
    color: PDF_COLOR.purple,
    letterSpacing: 0.5,
  },
  fitBoxText: { fontSize: PDF_TYPE.small, lineHeight: 1.45, marginTop: 2 },
  fitTag: {
    fontFamily: PDF_FONT.bold,
    fontSize: 7,
    color: PDF_COLOR.purple,
    marginLeft: 4,
  },

  // Perks & Quirks: four small cards to a row. Explicit widths and right
  // margins rather than space-between, so a short last row sits left instead
  // of spreading its cards across the page. 4 x 23.5% + 3 x 2% = 100%.
  perkGrid: { flexDirection: "row", flexWrap: "wrap", marginTop: 4 },
  perkCard: {
    width: "23.5%",
    marginRight: "2%",
    marginBottom: 8,
    padding: 7,
    borderWidth: 1,
    borderColor: PDF_COLOR.border,
    borderRadius: 6,
    backgroundColor: PDF_COLOR.white,
    // A column, so the answer can sit on the card's floor: the row stretches
    // every card to its tallest, and the chips then line up across it.
    flexDirection: "column",
  },
  perkCardEndOfRow: { marginRight: 0 },
  perkKind: {
    fontFamily: PDF_FONT.bold,
    fontSize: 7,
    color: PDF_COLOR.muted,
    letterSpacing: 0.6,
    textTransform: "uppercase",
  },
  perkName: {
    fontFamily: PDF_FONT.bold,
    fontSize: PDF_TYPE.body,
    lineHeight: 1.3,
    marginTop: 3,
    // The gap above the answer on the tallest card; the shorter ones get
    // more, from the answer's auto top margin.
    marginBottom: 6,
  },
  perkResponse: {
    alignSelf: "flex-start",
    marginTop: "auto",
    paddingVertical: 2,
    paddingHorizontal: 5,
    borderRadius: 3,
    fontFamily: PDF_FONT.bold,
    fontSize: 7,
  },
  /**
   * Both answers blue, as SmallPerkCard draws them.
   *
   * The card's `Rating` row is `#078dee` whether the climber said "That's for
   * me" or "Yikes" - only the thumb glyph changes. Colouring the two
   * differently here would have the document pass a judgement the Hat does not.
   */
  perkResponseAnswered: {
    backgroundColor: PDF_COLOR.blueSurface,
    color: PDF_COLOR.blue,
  },

  cardTitle: { fontFamily: PDF_FONT.bold, fontSize: PDF_TYPE.subheading },

  reflection: {
    marginTop: 8,
    padding: 8,
    backgroundColor: PDF_COLOR.surface,
    borderLeftWidth: 3,
    borderLeftColor: PDF_COLOR.blue,
    borderRadius: 2,
  },
  reflectionPrompt: {
    fontFamily: PDF_FONT.bold,
    fontSize: PDF_TYPE.small,
    color: PDF_COLOR.muted,
  },
  reflectionText: { fontSize: PDF_TYPE.body, lineHeight: 1.45, marginTop: 3 },

  spotlight: {
    marginTop: 8,
    padding: 8,
    backgroundColor: PDF_COLOR.greenSurface,
    borderRadius: 4,
  },
  shoutOuts: {
    marginTop: 8,
    padding: 8,
    backgroundColor: PDF_COLOR.amberSurface,
    borderRadius: 4,
  },
  shoutOutLabel: {
    fontFamily: PDF_FONT.bold,
    fontSize: PDF_TYPE.small,
    color: PDF_COLOR.amberText,
  },
  quote: { fontSize: PDF_TYPE.body, marginTop: 4 },
  quoteAuthor: {
    fontSize: PDF_TYPE.small,
    color: PDF_COLOR.muted,
    textAlign: "right",
  },

  // --- The identity band and the summary grid (page one) ---
  identity: {
    paddingBottom: 10,
    borderBottomWidth: 2,
    borderBottomColor: PDF_COLOR.border,
  },
  identityTop: {
    flexDirection: "row",
    alignItems: "flex-end",
    justifyContent: "space-between",
  },
  climberName: { fontFamily: PDF_FONT.bold, fontSize: 24, flex: 1 },
  identityMeta: {
    fontSize: PDF_TYPE.body,
    color: PDF_COLOR.muted,
    marginTop: 4,
  },

  /** The Hat, across the full width, as page one's one big statement. */
  hero: {
    flexDirection: "row",
    alignItems: "flex-start",
    marginTop: 13,
    padding: 13,
    borderWidth: 1,
    borderColor: PDF_COLOR.border,
    borderRadius: 8,
    backgroundColor: PDF_COLOR.surface,
  },
  /**
   * A circle. @react-pdf has no `borderRadius: "50%"`, so it is half the box -
   * change the size and this changes with it or the circle becomes a rounded
   * square.
   *
   * 72 AND NOT THE DESIGN'S 86, deliberately. The hero sets page one's height
   * and the reflection is the last thing on it, so these 14pt are the
   * difference between three lines of a climber's own writing fitting and
   * four. Measured: at 80 the fourth line moves the whole EXPLAIN YOUR
   * THINKING block to page two.
   */
  heroImage: { width: 72, height: 72, borderRadius: 36, marginRight: 14 },
  heroName: { fontFamily: PDF_FONT.bold, fontSize: 22, marginTop: 1 },
  heroText: {
    fontSize: PDF_TYPE.subheading,
    lineHeight: 1.4,
    marginTop: 6,
  },

  /**
   * A band heading - the uppercase caption that sits OUTSIDE a card and names
   * a part of the page. Distinct from `cardLabel`, the smaller overline inside
   * one: the band says which part of the plan you are reading, the label says
   * which field you are looking at.
   */
  band: {
    fontFamily: PDF_FONT.bold,
    fontSize: 9.5,
    color: PDF_COLOR.muted,
    letterSpacing: 0.7,
    textTransform: "uppercase",
    marginTop: 12,
    marginBottom: 5,
  },
  /** Two bands side by side, over two columns of cards. */
  bandRow: { flexDirection: "row" },

  /** The page-one grid: two columns with a gutter, each holding its own cards. */
  twoCol: { flexDirection: "row", alignItems: "flex-start" },
  colLeft: { width: "48%", marginRight: "4%" },
  colRight: { width: "48%" },

  /** A white bordered box. Everything on page one lives in one of these. */
  card: {
    padding: 9,
    borderWidth: 1,
    borderColor: PDF_COLOR.border,
    borderRadius: 6,
    backgroundColor: PDF_COLOR.white,
    marginBottom: 6,
  },
  cardRow: { flexDirection: "row", alignItems: "flex-start" },
  cardLabel: {
    fontFamily: PDF_FONT.bold,
    fontSize: 7,
    color: PDF_COLOR.muted,
    letterSpacing: 0.5,
    textTransform: "uppercase",
  },
  cardValue: {
    fontFamily: PDF_FONT.bold,
    fontSize: PDF_TYPE.subheading,
    marginTop: 2,
  },
  cardMeta: { fontSize: PDF_TYPE.small, color: PDF_COLOR.muted, marginTop: 1 },
  /** The "or" between two After High School paths, ruled either side. */
  orDivider: {
    flexDirection: "row",
    alignItems: "center",
    marginTop: 5,
    marginBottom: 3,
  },
  orRule: { flex: 1, height: 1, backgroundColor: PDF_COLOR.border },
  orText: {
    fontFamily: PDF_FONT.bold,
    fontSize: 7,
    color: PDF_COLOR.muted,
    letterSpacing: 0.5,
    textTransform: "uppercase",
    marginHorizontal: 6,
  },
  /** The program's own blurb, under its name. */
  cardText: {
    fontSize: PDF_TYPE.small,
    color: PDF_COLOR.muted,
    lineHeight: 1.45,
    marginTop: 6,
  },
  cardIcon: { width: 30, height: 30, marginRight: 8, objectFit: "contain" },

  /** One facet inside a My Profile card: its icon, then its name. */
  profileCardRow: {
    flexDirection: "row",
    alignItems: "center",
    marginTop: 6,
  },
  profileCardIcon: { width: 16, height: 16, marginRight: 7 },
  profileCardName: { fontFamily: PDF_FONT.bold, fontSize: PDF_TYPE.body },

  /** The climber's own results, in two labelled rows of small tiles. */
  profileRow: { flexDirection: "row", alignItems: "flex-start", marginTop: 5 },
  /**
   * A fixed-width caption, so the two rows of tiles start in the same place
   * whichever label is longer.
   */
  profileRowLabel: {
    width: 92,
    fontSize: 7,
    color: PDF_COLOR.muted,
    letterSpacing: 0.4,
    textTransform: "uppercase",
    marginTop: 3,
  },
  profileStrip: { flexDirection: "row", flexWrap: "wrap", flex: 1 },
  profileTile: {
    flexDirection: "row",
    alignItems: "center",
    marginRight: 14,
    marginTop: 4,
  },
  profileIcon: { width: 16, height: 16, borderRadius: 8, marginRight: 4 },
  profileName: { fontFamily: PDF_FONT.bold, fontSize: PDF_TYPE.small },

  /** What the climber has collected, as a row of counts. */
  tally: {
    flexDirection: "row",
    marginTop: 8,
    padding: 10,
    backgroundColor: PDF_COLOR.surface,
    borderRadius: 6,
  },
  tallyItem: { flex: 1 },
  tallyValue: { fontFamily: PDF_FONT.bold, fontSize: PDF_TYPE.heading },
  tallyLabel: {
    fontFamily: PDF_FONT.bold,
    fontSize: 7,
    color: PDF_COLOR.muted,
    letterSpacing: 0.5,
    textTransform: "uppercase",
    marginTop: 1,
  },

  /** A Strength or Goal pill over its skill's name: room for the name to breathe. */
  chipGroup: { flexDirection: "row", flexWrap: "wrap", marginBottom: 5 },
  /** The same gap on the Skills to Build page, where the name has no margin of its own. */
  underChip: { marginTop: 7 },
  /** A named value under a card's pill, with its type beneath it. */
  summaryItem: { marginTop: 3 },
  chipMeta: { fontSize: 7, color: PDF_COLOR.muted, marginTop: 1 },

  // Two to a row. Explicit widths and right margins rather than
  // space-between, so a lone card on the last row sits left instead of
  // stretching across the page. 2 x 48% + 4% = 100%.
  summaryGrid: { flexDirection: "row", flexWrap: "wrap", marginTop: 18 },
  summaryCard: {
    width: "48%",
    marginRight: "4%",
    marginBottom: 8,
    padding: 8,
    borderWidth: 1,
    borderColor: PDF_COLOR.border,
    borderRadius: 6,
    flexDirection: "row",
    alignItems: "flex-start",
  },
  summaryCardEndOfRow: { marginRight: 0 },
  summaryLabel: {
    fontFamily: PDF_FONT.bold,
    fontSize: 7,
    color: PDF_COLOR.muted,
    letterSpacing: 0.5,
    textTransform: "uppercase",
  },
  summaryValue: {
    fontFamily: PDF_FONT.bold,
    fontSize: PDF_TYPE.subheading,
    marginTop: 2,
  },
  summaryImage: {
    width: 30,
    height: 30,
    marginRight: 8,
    objectFit: "contain",
  },

  // --- Chips, as the Climb Plan's own cards wear them ---
  chipBase: {
    alignSelf: "flex-start",
    marginRight: 4,
    marginTop: 3,
    paddingVertical: 2,
    paddingHorizontal: 6,
    borderRadius: 6,
    fontFamily: PDF_FONT.bold,
    fontSize: 8,
    letterSpacing: 0.5,
    textTransform: "uppercase",
  },
  chipStrength: {
    backgroundColor: PDF_COLOR.strengthChipSurface,
    color: PDF_COLOR.strengthChipText,
  },
  chipGoal: { backgroundColor: PDF_COLOR.goalChip, color: PDF_COLOR.white },
  chipConsidering: {
    backgroundColor: PDF_COLOR.consideringChipSurface,
    color: PDF_COLOR.consideringChipText,
  },

  // --- Section titles, now with the module's own illustration ---
  sectionTitleRow: {
    flexDirection: "row",
    alignItems: "center",
    paddingBottom: 4,
    borderBottomWidth: 1,
    borderBottomColor: PDF_COLOR.border,
  },
  sectionIcon: { width: 14, height: 14, marginRight: 6 },

  /**
   * An icon that came from the CMS, at whatever ratio its author drew it.
   *
   * `objectFit: contain` is the point. The default crops to fill the box, so
   * a taller-than-wide icon loses its top and bottom - which is exactly how
   * the Keys to Success icons came out clipped.
   */
  cmsIcon: {
    width: 28,
    height: 28,
    marginRight: 8,
    objectFit: "contain",
  },
  cmsIconSmall: { width: 22, height: 22, objectFit: "contain" },

  pathRow: { flexDirection: "row", alignItems: "flex-start", marginTop: 6 },
  /** A name with its pill beside it - Considering, Goal - over its description. */
  chipTitleRow: { flexDirection: "row", alignItems: "center", marginBottom: 4 },
  pathIconSlot: { width: 28, alignItems: "center" },

  /** What Core Personality and Work Values are, for a first-time reader. */
  infoBox: {
    marginTop: 14,
    padding: 10,
    backgroundColor: PDF_COLOR.surface,
    borderRadius: 6,
  },
  infoItem: { marginTop: 4 },
  infoName: {
    fontFamily: PDF_FONT.bold,
    fontSize: PDF_TYPE.small,
  },
  infoText: {
    fontSize: PDF_TYPE.small,
    color: PDF_COLOR.muted,
    lineHeight: 1.45,
    marginTop: 1,
  },

  /** The muted blurb under a facet's name, from its CMS description. */
  facetDescription: {
    fontSize: PDF_TYPE.small,
    color: PDF_COLOR.muted,
    marginTop: 1,
  },

  // The same blurb when the CMS authored it as a list. A row per bullet so the
  // mark stays on the first line and the text wraps under itself rather than
  // back under the mark.
  facetBulletRow: { flexDirection: "row", marginTop: 1 },
  facetBulletMark: {
    fontSize: PDF_TYPE.small,
    color: PDF_COLOR.muted,
    width: 7,
  },
  facetBulletText: {
    fontSize: PDF_TYPE.small,
    color: PDF_COLOR.muted,
    flex: 1,
  },

  // --- The Schools / Credentials panels, from ProgramDetailBody ---
  panelRow: { flexDirection: "row", marginTop: 8 },
  panel: {
    flex: 1,
    padding: 8,
    borderWidth: 1,
    borderColor: PDF_COLOR.border,
    borderRadius: 8,
  },
  panelGap: { marginRight: 10 },
  panelHeading: { flexDirection: "row", alignItems: "center", marginBottom: 4 },
  panelHeadingIcon: { width: 12, height: 12, marginRight: 5 },
  panelHeadingText: {
    fontFamily: PDF_FONT.bold,
    fontSize: PDF_TYPE.body,
  },
  schoolRow: {
    marginTop: 4,
    paddingVertical: 4,
    paddingHorizontal: 6,
    borderRadius: 6,
    backgroundColor: PDF_COLOR.blueSurface,
  },
  schoolName: { fontFamily: PDF_FONT.bold, fontSize: PDF_TYPE.body },
  credentialRow: {
    marginTop: 4,
    paddingVertical: 4,
    paddingHorizontal: 6,
    borderRadius: 6,
    backgroundColor: PDF_COLOR.amberRowSurface,
  },

  // --- Sample Course Sequence ---
  // The screen clips each course into a chevron with `clip-path`, which
  // @react-pdf has no equivalent for. The slab and its point are drawn as two
  // adjoining pieces in one fill instead, which reads as the same arrow.
  // A FIXED HEIGHT is what makes that work: the drawn point cannot measure
  // itself against a box whose height depends on its label.
  courseRow: { flexDirection: "row", flexWrap: "wrap" },
  courseItem: { flexDirection: "row", marginTop: 4, marginRight: 5 },
  courseBox: {
    height: COURSE_HEIGHT,
    justifyContent: "center",
    paddingLeft: 8,
    paddingRight: 6,
    borderTopLeftRadius: 4,
    borderBottomLeftRadius: 4,
    backgroundColor: PDF_COLOR.blueSurfaceStrong,
  },
  courseLabel: { fontFamily: PDF_FONT.bold, fontSize: PDF_TYPE.small },

  // --- Connect Your Profile cards ---
  /** Says which path the climber connected through: Core or Building Blocks. */
  connectedLabel: {
    fontFamily: PDF_FONT.bold,
    fontSize: 7,
    color: PDF_COLOR.muted,
    letterSpacing: 0.5,
    textTransform: "uppercase",
    marginTop: 10,
  },
  blockHead: { flexDirection: "row", alignItems: "flex-start" },
  blockMark: { width: 14, height: 14, marginLeft: 6 },
  blockTypeRow: { flexDirection: "row", alignItems: "center", marginTop: 2 },
  blockTypeIcon: { width: 10, height: 10, marginRight: 4 },

  /** The signed name, as it was written in the sign dialog. */
  signature: {
    fontFamily: PDF_FONT.signature,
    fontSize: 24,
    color: PDF_COLOR.purple,
    marginTop: 4,
  },

  /** The skill a group of shout-outs was given for. */
  quoteFor: {
    fontFamily: PDF_FONT.bold,
    fontSize: 7,
    color: PDF_COLOR.amberText,
    letterSpacing: 0.4,
    textTransform: "uppercase",
    marginTop: 6,
  },
});

/**
 * Keep a word whole.
 *
 * @react-pdf hyphenates by default, which in a narrow column breaks a name
 * mid-word - "Certified SOLIDWORKS Associate - Additive Manufactur/ing".
 * Registering this globally would reach the career and course PDFs that share
 * the renderer, so it is passed per `<Text>` instead.
 */
const NO_HYPHENS = (word: string) => [word];

/** A section's title rule. `minPresenceAhead` keeps it off a page's last line. */
const SectionTitle = ({
  title,
  subtitle,
  icon,
}: {
  title: string;
  subtitle?: string;
  /** The module's own illustration, from `climbPlanPDFIcons`. */
  icon?: string;
}) => (
  <View minPresenceAhead={60}>
    <View style={styles.sectionTitleRow}>
      {!!icon && <Image style={styles.sectionIcon} src={icon} />}
      <Text style={styles.sectionTitle}>{title}</Text>
    </View>
    {!!subtitle && <Text style={styles.sectionSubtitle}>{subtitle}</Text>}
  </View>
);

type ChipTone = "strength" | "goal" | "considering";

const CHIP_TONE = {
  strength: styles.chipStrength,
  goal: styles.chipGoal,
  considering: styles.chipConsidering,
} as const;

/**
 * A Climb Plan pill, in the colour its own card wears: purple for a Strength,
 * green for a Goal, amber for a path the climber is Considering.
 */
const Chip = ({ label, tone }: { label: string; tone: ChipTone }) => (
  <Text style={[styles.chipBase, CHIP_TONE[tone]]}>{label}</Text>
);

/** One course in the sample sequence: a tinted slab ending in a point. */
const CourseChevron = ({ name }: { name: string }) => (
  <View style={styles.courseItem} wrap={false}>
    <View style={styles.courseBox}>
      <Text style={styles.courseLabel} hyphenationCallback={NO_HYPHENS}>
        {name}
      </Text>
    </View>
    <Svg width={9} height={COURSE_HEIGHT} viewBox={`0 0 9 ${COURSE_HEIGHT}`}>
      <Polygon
        points={`0,0 9,${COURSE_HEIGHT / 2} 0,${COURSE_HEIGHT}`}
        fill={PDF_COLOR.blueSurfaceStrong}
      />
    </Svg>
  </View>
);

/**
 * Only drawn when there is an answer, so the editor's "(optional)" marker is
 * dropped from the prompt — the same rule as the web review's panel.
 */
const Reflection = ({ prompt, text }: { prompt: string; text: string }) =>
  !text ? null : (
    <View style={styles.reflection} wrap={false}>
      <Text style={styles.reflectionPrompt}>{reviewPrompt(prompt)}</Text>
      <Text style={styles.reflectionText}>{tidyReflection(text)}</Text>
    </View>
  );

/**
 * One of the climber's own results, drawn the way the career-plan PDF draws
 * them: the facet's icon, its name with the level ranged right, and a bar
 * filled to the score in that facet's own CMS colour.
 *
 * The colour is the facet's own and not a palette constant, because it is the
 * colour the climber has seen against that result everywhere else - on the
 * profile, on the dashboard and on the Hat card.
 */
const ScoredFacet = ({
  name,
  description,
  bullets,
  iconSrc,
  tierLabel,
  percentage = 0,
  color,
  showBar = true,
}: {
  name: string;
  description?: string;
  bullets?: string[];
  iconSrc?: string;
  tierLabel?: string;
  percentage?: number;
  color?: string;
  /**
   * Work Values are drawn without one. Their scores decide WHICH two the
   * profile shows and are then not shown themselves, so a bar here would be
   * the document publishing a ranking the climber has never seen.
   */
  showBar?: boolean;
}) => (
  <View style={styles.scoredRow} wrap={false}>
    {!!iconSrc && <Image style={styles.scoredIcon} src={iconSrc} />}
    <View style={styles.rowBody}>
      <View style={styles.scoredHead}>
        <Text style={styles.scoredName}>{name}</Text>
        {/* With a bar, the level sits beside it instead - see below. */}
        {!showBar && !!tierLabel && (
          <Text style={styles.scoredLevel}>{tierLabel}</Text>
        )}
      </View>
      {/* The Core facets are authored as a lead-in and a list of fragments that
          complete it, so they print as the list they are — run together into a
          line they read as broken English whichever punctuation joins them.
          Work Values are authored as prose and fall through to the line. The
          two are the same content, so only one is ever drawn. */}
      {bullets && bullets.length > 0 ? (
        bullets.map((bullet) => (
          <View key={bullet} style={styles.facetBulletRow}>
            <Text style={styles.facetBulletMark}>{"\u2022"}</Text>
            <Text style={styles.facetBulletText}>{bullet}</Text>
          </View>
        ))
      ) : !!description ? (
        <Text style={styles.facetDescription}>{description}</Text>
      ) : null}
      {/* The level beside the bar it reads, so the measure and its summary
          are taken in together rather than across the description. */}
      {showBar && (
        <View style={styles.scoredBarRow}>
          <View style={styles.scoredTrack}>
            <View
              style={[
                styles.scoredFill,
                {
                  // Clamped: a facet scored above its own possible total would
                  // otherwise draw a bar wider than the track it sits in.
                  width: `${Math.max(0, Math.min(100, percentage))}%`,
                  backgroundColor: color || PDF_COLOR.muted,
                },
              ]}
            />
          </View>
          {!!tierLabel && (
            <Text style={[styles.scoredLevel, styles.scoredBarLevel]}>
              {tierLabel}
            </Text>
          )}
        </View>
      )}
    </View>
  </View>
);

const Facet = ({
  name,
  iconSrc,
  tierLabel,
  isMatch,
}: {
  name: string;
  iconSrc?: string;
  tierLabel?: string;
  isMatch?: boolean;
}) => (
  <View style={styles.facetRow} wrap={false}>
    {!!iconSrc && <Image style={styles.facetIcon} src={iconSrc} />}
    <Text style={styles.facetName}>{name}</Text>
    {!!tierLabel && <Text style={styles.facetTier}>{tierLabel}</Text>}
    {isMatch && <Text style={styles.fitTag}>{texts.labels.fit}</Text>}
  </View>
);

interface SummaryItem {
  name: string;
  /** The small grey line under it, such as a skill's type. */
  meta?: string;
}

/**
 * A band heading - see `styles.band`.
 *
 * `minPresenceAhead` is how a heading refuses to be the last thing on a page.
 * It belongs on the HEADING, not on a wrapper around the whole section: the
 * wrapper is the thing being split, so asking it for room ahead asks the
 * question too late.
 */
const Band = ({
  children,
  minPresenceAhead,
}: {
  children: string;
  minPresenceAhead?: number;
}) => (
  <Text style={styles.band} minPresenceAhead={minPresenceAhead}>
    {children}
  </Text>
);

/** One of the My Profile cards: a label, then a row per facet. */
const ProfileCard = ({
  label,
  facets,
}: {
  label: string;
  facets: Array<{ name: string; iconSrc?: string }>;
}) => (
  <View style={styles.card} wrap={false}>
    <Text style={styles.cardLabel}>{label}</Text>
    {facets.map((facet) => (
      <View key={facet.name} style={styles.profileCardRow}>
        {!!facet.iconSrc && (
          <Image style={styles.profileCardIcon} src={facet.iconSrc} />
        )}
        <Text style={styles.profileCardName}>{facet.name}</Text>
      </View>
    ))}
  </View>
);

/**
 * Who the climber is, as two cards side by side.
 *
 * THE STRONG ONES ONLY, and no descriptions or bars. A later page prints all
 * six Core types with their tiers, their bars and their blurbs; repeating any
 * of that here would be a second copy rather than a summary. Core is filtered
 * on the tier the score produced, not on the words the tile prints. The Work
 * Values are already the two the Profile shows - which two is
 * `computeTopFacets`' decision, and it stays the Profile's.
 */
const ProfileSummary = ({ data }: { data: ClimbPlanPDFData }) => {
  const highCore = data.myCore.filter((facet) => facet.tier === "high");
  // Both halves or neither: a lone card beside an empty column reads as a gap
  // in the plan rather than a summary of it.
  if (highCore.length === 0 || data.myWorkValues.length === 0) return null;

  return (
    <View wrap={false}>
      <Band>{texts.summary.myProfile}</Band>
      <View style={styles.twoCol}>
        <View style={styles.colLeft}>
          <ProfileCard
            label={texts.summary.highInterestCore}
            facets={highCore}
          />
        </View>
        <View style={styles.colRight}>
          <ProfileCard
            label={texts.summary.workValues}
            facets={data.myWorkValues}
          />
        </View>
      </View>
    </View>
  );
};

/** A labelled card: an overline, a bold value, and an optional blurb under it. */
const LabelledCard = ({
  label,
  value,
  text,
  iconSrc,
}: {
  label: string;
  value: string;
  text?: string;
  iconSrc?: string;
}) => (
  <View style={styles.card} wrap={false}>
    <View style={styles.cardRow}>
      {!!iconSrc && <Image style={styles.cardIcon} src={iconSrc} />}
      <View style={styles.rowBody}>
        <Text style={styles.cardLabel}>{label}</Text>
        <Text style={styles.cardValue} hyphenationCallback={NO_HYPHENS}>
          {value}
        </Text>
      </View>
    </View>
    {!!text && <Text style={styles.cardText}>{text}</Text>}
  </View>
);

/**
 * The After High School paths, one to a line with an "or" rule between them.
 *
 * A comma list read as a to-do list - every path, in order. The climber is
 * considering these as alternatives, and the divider says so.
 */
const PathsCard = ({ label, names }: { label: string; names: string[] }) => (
  <View style={styles.card} wrap={false}>
    <Text style={styles.cardLabel}>{label}</Text>
    {names.map((name, index) => (
      <View key={index}>
        {index > 0 && (
          <View style={styles.orDivider}>
            <View style={styles.orRule} />
            <Text style={styles.orText}>{texts.summary.or}</Text>
            <View style={styles.orRule} />
          </View>
        )}
        <Text style={styles.cardValue} hyphenationCallback={NO_HYPHENS}>
          {name}
        </Text>
      </View>
    ))}
  </View>
);

/** A card headed by a chip rather than an overline, and listing named items. */
const ChipCard = ({
  label,
  tone,
  items,
}: {
  label: string;
  tone: ChipTone;
  items: Array<{ name: string; meta?: string }>;
}) => (
  <View style={styles.card} wrap={false}>
    <View style={styles.chipGroup}>
      <Chip label={label} tone={tone} />
    </View>
    {items.map((item) => (
      <View key={item.name}>
        <Text style={styles.cardValue} hyphenationCallback={NO_HYPHENS}>
          {item.name}
        </Text>
        {!!item.meta && <Text style={styles.cardMeta}>{item.meta}</Text>}
      </View>
    ))}
  </View>
);

/**
 * High School Priorities: the chosen program on the left with its own blurb,
 * the school and the key to success stacked on the right.
 *
 * The program's description is the SAME `programIntro` its own page prints
 * later. Shown here too because page one is read on its own - handed to a
 * parent, or printed for a meeting - where a program name with nothing under
 * it says only that a choice was made.
 */
const PrioritiesBand = ({ data }: { data: ClimbPlanPDFData }) => {
  const program = data.chosen.program;
  const school = data.chosen.school;
  const key = data.keysToSuccess;
  if (!program && !school && !key) return null;

  return (
    <View wrap={false}>
      <Band>{texts.summary.highSchoolPriorities}</Band>
      <View style={styles.twoCol}>
        <View style={styles.colLeft}>
          {!!program && (
            <LabelledCard
              label={texts.summary.program}
              value={program}
              text={data.highSchoolPriorities?.programIntro || undefined}
            />
          )}
        </View>
        <View style={styles.colRight}>
          {!!school && (
            <LabelledCard label={texts.summary.highSchool} value={school} />
          )}
          {!!key && (
            <LabelledCard
              label={texts.summary.keyToSuccess}
              value={key.name}
              iconSrc={key.iconSrc}
            />
          )}
        </View>
      </View>
    </View>
  );
};

/**
 * Skill and After High School, as two bands over two columns.
 *
 * The bands sit in their own row so both headings share a baseline however
 * tall the cards beneath them turn out to be - a climber with three goals and
 * one path would otherwise push "After High School" down the page on its own.
 */
const SkillAndPathsBand = ({ data }: { data: ClimbPlanPDFData }) => {
  const strength = data.skillsToBuild?.strength;
  const goals = data.skillsToBuild?.goals ?? [];
  const paths = data.afterHighSchool;
  if (!strength && goals.length === 0 && paths.length === 0) return null;

  return (
    <View wrap={false}>
      <View style={styles.bandRow}>
        <View style={styles.colLeft}>
          {(!!strength || goals.length > 0) && (
            <Band>{texts.summary.skill}</Band>
          )}
        </View>
        <View style={styles.colRight}>
          {paths.length > 0 && <Band>{texts.summary.afterHighSchoolBand}</Band>}
        </View>
      </View>
      <View style={styles.twoCol}>
        <View style={styles.colLeft}>
          {!!strength && (
            <ChipCard
              label={texts.summary.strength}
              tone="strength"
              items={[{ name: strength.name, meta: strength.typeLabel }]}
            />
          )}
          {goals.length > 0 && (
            <ChipCard
              label={texts.summary.goals(goals.length)}
              tone="goal"
              items={goals.map((goal) => ({
                name: goal.name,
                meta: goal.typeLabel,
              }))}
            />
          )}
        </View>
        <View style={styles.colRight}>
          {paths.length > 0 && (
            <PathsCard
              label={texts.summary.afterHighSchool}
              names={paths.map((path) => path.name)}
            />
          )}
        </View>
      </View>
    </View>
  );
};

/**
 * A climber's Climb Plan, as a printable document.
 *
 * PURE PROPS, NO HOOKS — @react-pdf renders in its own reconciler outside the
 * React tree, so there is no context to read and no query to await. Everything
 * arrives already flattened and already decided by `buildClimbPlanPDFData`:
 * a section this climber has nothing for is `null` here, and this file never
 * re-derives a rule the review screen owns.
 *
 * PAGINATION IS @react-pdf'S, NOT HAND-COUNTED. The two older PDFs in this
 * directory compute their own page breaks by counting rows, which works
 * because their content is fixed. This one has eight optional sections, four
 * free-text reflections and lists of unbounded length, so the content flows
 * through one `<Page>` and the chrome repeats via `fixed`. The page's padding
 * reserves the header and footer heights — without that, page two starts
 * underneath the logo, because a fixed element repeats without displacing
 * anything.
 *
 * A SECTION NEVER SPLITS ACROSS PAGES. Every section's outer `<View>` is
 * `wrap={false}`, so one that does not fit in what is left of a page starts
 * the next page whole. The limit is @react-pdf's, and it is sharp: a no-wrap
 * block TALLER than a page is neither split nor moved — it runs off the bottom
 * and is clipped. Every section here is bounded well under a page by the
 * product itself (three blocks, two goals, 400-character reflections, the
 * CMS's fixed counts), which is what makes keeping them whole safe. A section
 * that ever gains an unbounded list needs that list broken into its own
 * atomic chunks instead.
 */
export const ClimbPlanPDF: React.FC<{ data: ClimbPlanPDFData }> = ({
  data,
}) => (
  <Document title={`${texts.footer.documentTitle} - ${data.climber.name}`}>
    <Page size="LETTER" style={styles.page}>
      <View style={styles.accentBar} fixed />
      <View style={styles.header} fixed>
        <Image style={styles.logo} src={HNLNavLogo} />
        {/* Who the plan belongs to. The date moved to the footer, beside the
            document's name, where the other chrome lives. */}
        <Text style={styles.headerRight}>
          {[
            data.climber.name,
            data.climber.studentId
              ? `${texts.header.studentIdLabel} ${data.climber.studentId}`
              : null,
          ]
            .filter(Boolean)
            .join(" | ")}
        </Text>
      </View>

      {/* The only legal place to read the page number. */}
      <View style={styles.footer} fixed>
        <Text>
          {[
            texts.footer.documentTitle,
            data.lastUpdated
              ? `${texts.header.updatedLabel} ${data.lastUpdated}`
              : null,
          ]
            .filter(Boolean)
            .join(" | ")}
        </Text>
        <Text
          render={({ pageNumber, totalPages }) =>
            texts.footer.page(pageNumber, totalPages)
          }
        />
      </View>

      {/* --- PAGE ONE: the whole plan at a glance --- */}
      <View style={styles.identity} wrap={false}>
        <View style={styles.identityTop}>
          <Text style={styles.climberName}>
            {texts.identity.title(data.climber.name)}
          </Text>
        </View>
        {/* The Student ID is in the header now, so it is not repeated here. */}
        <Text style={styles.identityMeta}>
          {[
            data.climber.gradeLabel,
            data.climber.districtName,
            data.climber.schoolName,
          ]
            .filter(Boolean)
            .join(" | ")}
        </Text>
      </View>

      {/* The Hat, with room for its picture and its own words. */}
      {!!data.topHat && (
        <View style={styles.hero} wrap={false}>
          {!!data.topHat.imageSrc && (
            <Image style={styles.heroImage} src={data.topHat.imageSrc} />
          )}
          <View style={styles.rowBody}>
            <Text style={styles.cardLabel}>{texts.summary.topHat}</Text>
            <Text style={styles.heroName}>{data.topHat.displayName}</Text>
            {!!data.topHat.shortDescription && (
              <Text style={styles.heroText}>
                {data.topHat.shortDescription}
              </Text>
            )}
          </View>
        </View>
      )}

      <ProfileSummary data={data} />

      <PrioritiesBand data={data} />

      <SkillAndPathsBand data={data} />

      {/* ATOMIC, deliberately. Letting this flow strands the heading alone at
          the foot of the page - `minPresenceAhead` does not hold it back,
          whether it is set here or on the heading itself. So the section
          moves as one or not at all, and the spacing above is what keeps a
          realistic plan on page one. */}
      {!!data.reflection && (
        <View wrap={false}>
          <Band>{texts.summary.reflection}</Band>
          <Reflection
            prompt={data.reflection.prompt}
            text={data.reflection.text}
          />
        </View>
      )}

      {/* Everything above is the summary; the detail starts on page two. */}
      <View break />

      {/* --- The climber's own Core Personality and Work Values --- */}
      {(data.myCore.length > 0 || data.myWorkValues.length > 0) && (
        <View style={styles.section} wrap={false}>
          <SectionTitle
            title={texts.sections.myProfile}
            subtitle={texts.sections.myProfileSubtitle}
          />
          <View style={styles.twoColumn}>
            {data.myCore.length > 0 && (
              // A climber who has not done Work Values leaves half the page
              // blank beside their Core; the Core takes the whole width
              // instead of sitting in a column that has nothing to pair with.
              <View
                style={data.myWorkValues.length > 0 ? styles.half : styles.full}
              >
                <Text style={styles.subheading}>{texts.identity.myCore}</Text>
                {data.myCore.map((facet) => (
                  <ScoredFacet key={facet.name} {...facet} />
                ))}
              </View>
            )}
            {data.myWorkValues.length > 0 && (
              <View style={styles.half}>
                <Text style={styles.subheading}>
                  {texts.identity.myWorkValues}
                </Text>
                {data.myWorkValues.map((facet) => (
                  <ScoredFacet key={facet.name} {...facet} showBar={false} />
                ))}
              </View>
            )}
          </View>

          {/* The two lists above are this climber's scores; this says what is
              being scored, for a parent or counsellor meeting it cold. */}
          <View style={styles.infoBox} wrap={false}>
            {texts.sections.whatTheseAre.map((item) => (
              <View key={item.name} style={styles.infoItem}>
                <Text style={styles.infoName}>{item.name}</Text>
                <Text style={styles.infoText}>{item.text}</Text>
              </View>
            ))}
          </View>
        </View>
      )}

      {/* --- Top Hat --- */}
      {!!data.topHat && (
        <View style={styles.section} wrap={false}>
          <SectionTitle
            title={texts.sections.topHat}
            subtitle={texts.sections.topHatSubtitle}
            icon={icons.topHat}
          />
          <View style={[styles.row, { marginTop: 8 }]} wrap={false}>
            {!!data.topHat.imageSrc && (
              <Image
                style={{ width: 48, height: 48, marginRight: 10 }}
                src={data.topHat.imageSrc}
              />
            )}
            <View style={styles.rowBody}>
              <Text style={styles.heading}>{data.topHat.displayName}</Text>
              <Text style={styles.body}>{data.topHat.shortDescription}</Text>
            </View>
          </View>

          <View style={styles.twoColumn}>
            <View style={styles.half}>
              <Text style={styles.subheading}>
                {texts.sections.corePersonalityTypes}
              </Text>
              <Text style={styles.caption}>
                {texts.sections.corePersonalitySubtitle}
              </Text>
              {data.topHat.core.map((entry) => (
                <Facet key={entry.name} {...entry} />
              ))}
            </View>
            {data.topHat.workValues.length > 0 && (
              <View style={styles.half}>
                <Text style={styles.subheading}>
                  {texts.sections.workValues}
                </Text>
                <Text style={styles.caption}>
                  {texts.sections.workValuesSubtitle}
                </Text>
                {data.topHat.workValues.map((entry) => (
                  <Facet key={entry.name} {...entry} />
                ))}
                {!!data.topHat.fitMessage && (
                  <View style={styles.fitBox} wrap={false}>
                    <Text style={styles.fitBoxLabel}>{texts.labels.fit}</Text>
                    <Text style={styles.fitBoxText}>
                      {data.topHat.fitMessage}
                    </Text>
                  </View>
                )}
              </View>
            )}
          </View>

          {data.topHat.perksAndQuirks.length > 0 && (
            <View>
              <Text style={styles.subheading}>
                {texts.sections.perksAndQuirks}
              </Text>
              <View style={styles.perkGrid}>
                {data.topHat.perksAndQuirks.map((item, index) => (
                  <View
                    key={`${item.kind}-${item.name}`}
                    style={[
                      styles.perkCard,
                      index % 4 === 3 ? styles.perkCardEndOfRow : {},
                    ]}
                    wrap={false}
                  >
                    <Text style={styles.perkKind}>{item.kind}</Text>
                    {/* No hyphenation: at a quarter of the page, @react-pdf's
                        default breaks a word in two on most lines. */}
                    <Text
                      style={styles.perkName}
                      hyphenationCallback={NO_HYPHENS}
                    >
                      {item.name}
                    </Text>
                    {!!item.response && (
                      <Text
                        style={[
                          styles.perkResponse,
                          styles.perkResponseAnswered,
                        ]}
                      >
                        {item.response}
                      </Text>
                    )}
                  </View>
                ))}
              </View>
            </View>
          )}

          {data.topHat.badges.length > 0 && (
            <View>
              <Text style={styles.subheading}>
                {texts.sections.personalityBadges}
              </Text>
              {data.topHat.badges.map((badge) => (
                <View key={badge.name} wrap={false}>
                  <Text style={styles.body}>{badge.name}</Text>
                  {!!badge.tooltip && (
                    <Text style={styles.cardMeta}>{badge.tooltip}</Text>
                  )}
                </View>
              ))}
            </View>
          )}
        </View>
      )}

      {/* --- Connect Your Profile --- */}
      <View style={styles.section} wrap={false}>
        <SectionTitle
          title={texts.sections.connectProfile}
          subtitle={texts.sections.connectProfileSubtitle}
          icon={icons.connectProfile}
        />
        {/* Which path the climber took. With no blocks connected the
            reflection is about their Core instead - the same rule as
            `showsCoreAndValues`, read off what the data builder kept. */}
        <Text style={styles.connectedLabel}>
          {data.connectProfile.blocks.length > 0
            ? texts.labels.blocksConnected
            : texts.labels.coreConnected}
        </Text>
        {data.connectProfile.blocks.map((block, index) => (
          <View
            key={`${block.activityName}-${index}`}
            style={styles.card}
            wrap={false}
          >
            <View style={styles.blockHead}>
              <View style={styles.rowBody}>
                <Text style={styles.cardTitle}>{block.activityName}</Text>
                <View style={styles.blockTypeRow}>
                  {!!block.categoryIconSrc && (
                    <Image
                      style={styles.blockTypeIcon}
                      src={block.categoryIconSrc}
                    />
                  )}
                  <Text style={styles.cardMeta}>
                    {[block.categoryName, block.duration]
                      .filter(Boolean)
                      .join(" | ")}
                  </Text>
                </View>
              </View>
              <Image style={styles.blockMark} src={icons.blocks} />
            </View>
            {!!block.description && (
              <Text style={styles.paragraph}>{block.description}</Text>
            )}
            {block.likes.length > 0 && (
              <Text style={styles.cardMeta}>
                {texts.labels.likes} {block.likes.join(", ")}
              </Text>
            )}
            {block.dislikes.length > 0 && (
              <Text style={styles.cardMeta}>
                {texts.labels.dislikes} {block.dislikes.join(", ")}
              </Text>
            )}
          </View>
        ))}
        <Reflection
          prompt={data.connectProfile.reflectionPrompt}
          text={data.connectProfile.reflection}
        />
      </View>

      {/* --- High School Priorities --- */}
      {!!data.highSchoolPriorities && (
        <View style={styles.section} wrap={false}>
          <SectionTitle
            title={texts.sections.highSchoolPriorities}
            subtitle={texts.sections.highSchoolPrioritiesSubtitle}
            icon={icons.highSchoolPriorities}
          />
          <Text style={[styles.heading, { marginTop: 8 }]}>
            {data.highSchoolPriorities.programName}
          </Text>
          {!!data.highSchoolPriorities.programIntro && (
            <Text style={styles.paragraph}>
              {data.highSchoolPriorities.programIntro}
            </Text>
          )}

          {/* Two panels side by side, as the program detail draws them. The
              screen shows three schools and hides the rest behind a "+N"
              tooltip; a page has no height to run out of, so every school is
              listed. With no credentials the schools panel takes the width,
              which is what the screen does too. */}
          {(data.highSchoolPriorities.schools.length > 0 ||
            data.highSchoolPriorities.certifications.length > 0) && (
            <View style={styles.panelRow}>
              {data.highSchoolPriorities.schools.length > 0 && (
                <View
                  style={[
                    styles.panel,
                    data.highSchoolPriorities.certifications.length > 0
                      ? styles.panelGap
                      : {},
                  ]}
                >
                  <View style={styles.panelHeading}>
                    <Image
                      style={styles.panelHeadingIcon}
                      src={icons.schoolPin}
                    />
                    <Text style={styles.panelHeadingText}>
                      {texts.sections.availableSchools}
                    </Text>
                  </View>
                  {data.highSchoolPriorities.schools.map((school) => (
                    <View key={school} style={styles.schoolRow} wrap={false}>
                      <Text
                        style={styles.schoolName}
                        hyphenationCallback={NO_HYPHENS}
                      >
                        {school}
                      </Text>
                    </View>
                  ))}
                </View>
              )}

              {data.highSchoolPriorities.certifications.length > 0 && (
                <View style={styles.panel}>
                  <View style={styles.panelHeading}>
                    <Image
                      style={styles.panelHeadingIcon}
                      src={icons.credentialMedal}
                    />
                    <Text style={styles.panelHeadingText}>
                      {texts.sections.availableCredentials}
                    </Text>
                  </View>
                  {data.highSchoolPriorities.certifications.map((cert) => (
                    <View
                      key={cert.name}
                      style={styles.credentialRow}
                      wrap={false}
                    >
                      <Text
                        style={styles.schoolName}
                        hyphenationCallback={NO_HYPHENS}
                      >
                        {cert.name}
                      </Text>
                      {!!cert.description && (
                        <Text
                          style={styles.facetDescription}
                          hyphenationCallback={NO_HYPHENS}
                        >
                          {cert.description}
                        </Text>
                      )}
                    </View>
                  ))}
                </View>
              )}
            </View>
          )}

          {data.highSchoolPriorities.sampleCourses.length > 0 && (
            <View>
              <Text style={styles.subheading}>
                {texts.sections.sampleCourses}
              </Text>
              <View style={styles.courseRow}>
                {data.highSchoolPriorities.sampleCourses.map(
                  (course, index) => (
                    <CourseChevron key={`${course}-${index}`} name={course} />
                  ),
                )}
              </View>
            </View>
          )}

          {data.highSchoolPriorities.otherHats.length > 0 && (
            <View>
              <Text style={styles.subheading}>{texts.sections.otherHats}</Text>
              {data.highSchoolPriorities.otherHats.map((hat) => (
                <Text key={hat.name} style={styles.body}>
                  {hat.name}
                  {hat.sentimentLabel ? ` | ${hat.sentimentLabel}` : ""}
                </Text>
              ))}
            </View>
          )}
        </View>
      )}

      {/* --- Program Spotlight --- */}
      {!!data.programSpotlight && (
        <View style={styles.section} wrap={false}>
          <SectionTitle
            title={texts.sections.programSpotlight}
            icon={icons.programSpotlight}
          />
          <View style={styles.spotlight}>
            {data.programSpotlight.map((paragraph, index) => (
              <Text key={index} style={styles.paragraph}>
                {paragraph}
              </Text>
            ))}
          </View>
        </View>
      )}

      {/* --- Keys to Success --- */}
      {!!data.keysToSuccess && (
        <View style={styles.section} wrap={false}>
          <SectionTitle
            title={texts.sections.keysToSuccess}
            subtitle={texts.sections.keysToSuccessSubtitle}
            icon={icons.keysToSuccess}
          />
          <View style={[styles.row, { marginTop: 8 }]} wrap={false}>
            {!!data.keysToSuccess.iconSrc && (
              <Image style={styles.cmsIcon} src={data.keysToSuccess.iconSrc} />
            )}
            <View style={styles.rowBody}>
              <View style={styles.chipTitleRow}>
                <Text style={styles.cardTitle}>{data.keysToSuccess.name}</Text>
                <View style={{ marginLeft: 6 }}>
                  <Chip label={data.keysToSuccess.goalLabel} tone="goal" />
                </View>
              </View>
              {!!data.keysToSuccess.description && (
                <Text style={styles.body}>
                  {data.keysToSuccess.description}
                </Text>
              )}
            </View>
          </View>
          <Reflection
            prompt={data.keysToSuccess.reflectionPrompt}
            text={data.keysToSuccess.reflection}
          />
        </View>
      )}

      {/* --- CTSOs --- */}
      {data.ctsos.length > 0 && (
        <View style={styles.section} wrap={false}>
          <SectionTitle
            title={texts.sections.ctsos}
            subtitle={texts.sections.ctsosSubtitle}
            icon={icons.ctsos}
          />
          {data.ctsos.map((ctso) => (
            <View key={ctso.name} style={{ marginTop: 6 }}>
              <Text style={styles.facetName}>{ctso.name}</Text>
              {ctso.paragraphs.map((paragraph, index) => (
                <Text key={index} style={styles.paragraph}>
                  {paragraph}
                </Text>
              ))}
            </View>
          ))}
        </View>
      )}

      {/* --- After High School --- */}
      {data.afterHighSchool.length > 0 && (
        <View style={styles.section} wrap={false}>
          <SectionTitle
            title={texts.sections.afterHighSchool}
            subtitle={texts.sections.afterHighSchoolSubtitle}
            icon={icons.afterHighSchool}
          />
          {data.afterHighSchool.map((path) => (
            <View key={path.name} style={styles.pathRow} wrap={false}>
              {/* A fixed slot, drawn empty when a path has no icon, so the
                  paths that do and the paths that do not start their text in
                  the same place. */}
              <View style={styles.pathIconSlot}>
                {!!path.iconSrc && (
                  <Image style={styles.cmsIconSmall} src={path.iconSrc} />
                )}
              </View>
              <View style={styles.rowBody}>
                {/* Every path here is one the climber chose, so every one
                    wears the chip — as every card on the review does. */}
                <View style={styles.chipTitleRow}>
                  <Text style={styles.facetName}>{path.name}</Text>
                  <View style={{ marginLeft: 6 }}>
                    <Chip label={texts.labels.considering} tone="considering" />
                  </View>
                </View>
                {/* Authored on the `hat-paths` CMS record and looked up live,
                    so a plan written before the text existed prints it too.
                    Conditional because a path whose description is unauthored,
                    or which the hat has since dropped, has none. */}
                {!!path.definition && (
                  <Text style={styles.body}>{path.definition}</Text>
                )}
              </View>
            </View>
          ))}
          <Reflection
            prompt={data.afterHighSchoolReflection.prompt}
            text={data.afterHighSchoolReflection.text}
          />
        </View>
      )}

      {/* --- Skills to Build --- */}
      {!!data.skillsToBuild && (
        <View style={styles.section} wrap={false}>
          <SectionTitle
            title={texts.sections.skillsToBuild}
            subtitle={texts.sections.skillsToBuildSubtitle}
            icon={icons.skillsToBuild}
          />
          {!!data.skillsToBuild.strength && (
            <View style={styles.card} wrap={false}>
              <Chip label={texts.sections.strength} tone="strength" />
              <Text style={[styles.cardTitle, styles.underChip]}>
                {data.skillsToBuild.strength.name}
              </Text>
              <Text style={styles.cardMeta}>
                {data.skillsToBuild.strength.typeLabel}
              </Text>
              {!!data.skillsToBuild.strength.description && (
                <Text style={styles.paragraph}>
                  {data.skillsToBuild.strength.description}
                </Text>
              )}
            </View>
          )}

          {!!data.skillsToBuild.shoutOutLabel && (
            <View style={styles.shoutOuts}>
              <Text style={styles.shoutOutLabel}>
                {data.skillsToBuild.shoutOutLabel}
              </Text>
              {data.skillsToBuild.shoutOuts.map((shoutOut, index) => (
                <View key={index} wrap={false}>
                  <Text style={styles.quote}>"{shoutOut.message}"</Text>
                  {!!shoutOut.author && (
                    <Text style={styles.quoteAuthor}>{shoutOut.author}</Text>
                  )}
                </View>
              ))}
            </View>
          )}

          {data.skillsToBuild.goals.length > 0 && (
            <View style={styles.twoColumn}>
              {data.skillsToBuild.goals.map((goal) => (
                <View key={goal.name} style={styles.half} wrap={false}>
                  <Chip label={texts.sections.goal} tone="goal" />
                  <Text style={[styles.facetName, styles.underChip]}>
                    {goal.name}
                  </Text>
                  <Text style={styles.cardMeta}>{goal.typeLabel}</Text>
                  {!!goal.description && (
                    <Text style={styles.body}>{goal.description}</Text>
                  )}
                </View>
              ))}
            </View>
          )}

          <Reflection
            prompt={data.skillsToBuild.reflectionPrompt}
            text={data.skillsToBuild.reflection}
          />
        </View>
      )}

      {/* --- Every other Shout Out this climber has been given --- */}
      {data.otherShoutOuts.length > 0 && (
        <View style={styles.section} wrap={false}>
          <SectionTitle
            title={texts.sections.shoutOuts}
            subtitle={texts.sections.shoutOutsSubtitle}
          />
          <View style={styles.shoutOuts}>
            {/* One heading per skill, its quotes beneath — never a caption
                per quote, which let an uncaptioned quote read as the
                previous skill's. */}
            {data.otherShoutOuts.map((group, groupIndex) => (
              <View key={groupIndex} wrap={false}>
                <Text style={styles.quoteFor}>{group.heading}</Text>
                {group.shoutOuts.map((shoutOut, index) => (
                  <View key={index}>
                    <Text style={styles.quote}>"{shoutOut.message}"</Text>
                    {!!shoutOut.author && (
                      <Text style={styles.quoteAuthor}>{shoutOut.author}</Text>
                    )}
                  </View>
                ))}
              </View>
            ))}
          </View>
        </View>
      )}

      {/* The signed name and date, which the review screen never shows. */}
      {!!data.signature && (
        <View style={[styles.section, { marginTop: 20 }]} wrap={false}>
          <Text style={styles.sectionTitle}>{texts.identity.signedBy}</Text>
          <Text style={styles.signature}>{data.signature.name}</Text>
          <Text style={styles.cardMeta}>{data.signature.signedOn}</Text>
        </View>
      )}
    </Page>
  </Document>
);

export default ClimbPlanPDF;
